import React, { useCallback } from 'react';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import { AddIcon, EditIcon } from 'base/components/Icon';
import { getDeleteColumn, getDownloadColumn, getEditColumn } from 'base/components/table/RowActionButton';
import DataTable from 'base/components/table/DataTable';
import withGlobalFilter from 'base/components/table/withGlobalFilter';
import theme from 'theme/theme';
import { getIsAdmin } from 'auth/store/auth/selectors';
import { setEditingUserId } from 'auth/store/users/actions';
import MainTitle from 'base/components/MainTitle';
import Header from 'base/components/Header';
import Button from 'base/components/Button';
import ButtonGroup from 'base/components/ButtonGroup';
import {
  useDownloadContactVcardMutation,
  useGetContactsQuery,
  useGetMyContactQuery,
} from 'contacts/store/contactService';
import toast from 'base/components/Toast/toast';
import {
  ContactInfoColumn,
  DivisionColumn,
  FirstNameColumn,
  LastNameColumn,
  PositionColumn,
  ProfilePictureColumn,
  SalutationColumn,
  UserCell,
} from '../ContactTableComponents';
import { openModal } from '../../../modal';
import { setActiveContactId } from '../../store/actions';
import ContactsTableSkeleton from '../../components/ContactsTableSkeleton';

const FilteredTable = withGlobalFilter(DataTable);

export default function ContactsPage() {
  const dispatch = useDispatch();
  const isAdmin = useSelector(getIsAdmin);
  const { data: contacts = [], isLoading } = useGetContactsQuery();
  const { data: myContact } = useGetMyContactQuery();
  const [downloadVcard] = useDownloadContactVcardMutation();

  const handleEdit = useCallback(
    data => {
      dispatch(setEditingUserId(data.user.id || null));
      dispatch(setActiveContactId(data.id));
      dispatch(openModal('CONTACT_EDIT'));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    data => {
      dispatch(setActiveContactId(data.id));
      dispatch(openModal('CONTACT_DELETE'));
    },
    [dispatch],
  );

  const handleDownload = useCallback(
    async data => {
      try {
        const blob = await downloadVcard(data.id).unwrap();
        saveAs(blob, `${data.lastName}.vcf`);
      } catch (e) {
        toast.error('Error downloading vcard');
      }
    },
    [downloadVcard],
  );

  const handleCreate = useCallback(() => {
    dispatch(setEditingUserId(null));
    dispatch(openModal('CONTACT_CREATE'));
  }, [dispatch]);

  const myUserString = myContact && myContact.user && myContact.user.toString;
  const handleEditMyContact = useCallback(() => {
    dispatch(setEditingUserId(myContact?.user || null));
    dispatch(openModal('MY_CONTACT_EDIT'));
  }, [dispatch, myUserString]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    ProfilePictureColumn,
    SalutationColumn,
    LastNameColumn,
    FirstNameColumn,
    ContactInfoColumn,
    PositionColumn,
    DivisionColumn,
    {
      Header: 'User',
      accessor: 'user',
      Cell: UserCell,
    },
    getDownloadColumn(handleDownload),
  ];
  if (isAdmin) {
    columns.push(getEditColumn(handleEdit), getDeleteColumn(handleDelete));
  }

  return (
    <>
      <Header>
        <MainTitle>Contacts</MainTitle>
        <ButtonGroup pullRight>
          {isAdmin && (
            <Button onClick={handleCreate}>
              <AddIcon color={theme.colors.medium} /> New contact
            </Button>
          )}
          {myContact && (
            <Button onClick={handleEditMyContact}>
              <EditIcon color={theme.colors.medium} /> Edit my contact info
            </Button>
          )}
        </ButtonGroup>
      </Header>
      <FilteredTable
        data={contacts}
        columns={columns}
        loading={isLoading}
        skeleton={ContactsTableSkeleton}
        filterKeys={['firstName', 'lastName', 'contactinfo', 'position', 'division']}
        filterLabel="Filter by name, email, function or division..."
        defaultSorted={[
          {
            id: 'lastName',
            desc: false,
          },
        ]}
      />
    </>
  );
}
